import React from 'react'
import classNames from 'classnames'
import { formatNumber } from '../utils/countDictinary'
import { uniqueId } from 'lodash-es'
import { IFieldProps } from './Field'
import { useDebounce } from 'usehooks-ts'
import { ReactSVG } from 'react-svg'
import CheckboxControl from './CheckboxControl'
import { getRemSize } from '../utils/getRemSize'
import { gsap } from 'gsap'

export interface SelectProps extends IFieldProps {
  value?: any
  onChange?: (value: any) => void
  options?: any
  dense?: boolean
  multiple?: boolean
  searchable?: boolean
  name?: string
  asyncOptions?: (query: string) => Promise<Array<any>>
  onClose?: () => void
  fullValue?: boolean
}

const Select: React.FC<SelectProps> = ({
  options = [],
  className = '',
  dense,
  multiple,
  value,
  label,
  name = 'select',
  searchable,
  required,
  asyncOptions,
  onChange,
  onClose,
  disabled,
  fullValue
}) => {
  const id = React.useRef(uniqueId(name))

  const [selected, setSelected] = React.useState<any | Array<any>>(
    value ?? (multiple ? [] : '')
  )
  const [search, setSearch] = React.useState('')
  const debouncedSearch = useDebounce(search, 500)
  const [open, setOpen] = React.useState(false)
  const [_options, setOptions] = React.useState<Array<any>>(options)
  const elementRef = React.useRef<HTMLDivElement>(null)
  const dropDownRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const toggle = React.useRef<any>(null)
  const getSelectedInputValue = (selected: any): string => {
    if (!multiple && selected) {
      const option = _options.find(
        (option: any) => (option?.value ?? option) === selected
      )

      if (!option) {
        return selected.label ?? selected
      }

      return option?.label ?? option ?? selected
    } else if (multiple && Array.isArray(selected) && selected.length) {
      const option = _options.find(
        (option: any) => (option?.value ?? option) === selected[0]
      )

      return selected.length === 1
        ? option?.label ?? option ?? selected[0]
        : `${selected.length} ${formatNumber(selected.length, 'variants')}`
    }

    return dense ? label ?? '' : ''
  }
  const [inputValue, setInputValue] = React.useState<string>(
    getSelectedInputValue(selected)
  )

  React.useEffect(() => {
    // if (!debouncedSearch) {
    //   setOptions(options)
    //   return
    // }

    const handler = async () => {
      let _options: Array<any> = []

      if (!asyncOptions) {
        _options = options.filter((option: any) =>
          (option?.label ?? option)
            .toLowerCase()
            .includes(debouncedSearch.toLowerCase())
        )
      } else {
        _options = await asyncOptions(debouncedSearch)
      }

      setOptions(_options)
    }

    void handler()
  }, [debouncedSearch, asyncOptions ? undefined : options])

  const isOptionSelected = (option: any): boolean => {
    if (multiple) {
      return !!selected.find((i: any) => i === (option?.value ?? option))
    }

    return selected === (option?.value ?? option)
  }
  const clickHandler = (e: any): void => {
    if (!e.target.closest(`[data-select="${id.current}"]`)) {
      setOpen(false)
    }
  }
  const keyupHandler = (e: any): void => {
    if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  React.useEffect(() => {
    const reposition = () => {
      const el = elementRef.current
      const dropdown = dropDownRef.current

      if (!el || !dropdown) return

      const elRect = el.getBoundingClientRect()
      const dwRect = dropdown.getBoundingClientRect()
      const dwHeight = dwRect.height
      const dwWidth = dwRect.width
      const isMobile = window.matchMedia('(max-width: 767px)')

      let gap = isMobile ? 0.6 : 0.8
      gap *= getRemSize()

      dropdown.style.setProperty('--max-width', elRect.width + 'px')

      const elToBottom =
        document.body.clientHeight - (elRect.top + scrollY + elRect.height + 20)

      if (dwHeight + gap <= elToBottom) {
        dropdown.style.setProperty('top', elRect.height + gap + 'px')
      } else {
        dropdown.style.setProperty('top', -dwHeight - gap + 'px')
      }

      const elToRight = document.body.clientWidth - (elRect.left + scrollX + 20)

      if (dwWidth <= elToRight) {
        dropdown.style.setProperty('left', 0 + 'px')
      } else {
        dropdown.style.setProperty('left', elRect.width - dwWidth + 'px')
      }
    }

    const observer = new ResizeObserver(reposition)

    toggle.current = (value: boolean) => {
      const dropdown = dropDownRef.current
      if (!dropdown) return

      if (value) {
        reposition()
        observer.observe(dropdown)
        window.addEventListener('resize', reposition)
        window.addEventListener('click', clickHandler)
        window.addEventListener('keyup', keyupHandler)
      } else {
        onClose?.()
        observer.unobserve(dropdown)
        window.removeEventListener('resize', reposition)
        window.removeEventListener('click', clickHandler)
        window.removeEventListener('keyup', keyupHandler)

        inputRef.current?.blur()
      }

      const tl = gsap.timeline()
      tl.set(dropdown, { willChange: 'transform, opacity' })

      if (value) {
        tl.set(dropdown, { visibility: 'visible', pointerEvents: 'all' })
      }

      tl.to(
        dropdown,
        { y: value ? 0 : 15, opacity: value ? 1 : 0, duration: 0.2 },
        '>0.15'
      )

      tl.set(dropdown, { willChange: 'auto' })
      if (!value) {
        tl.set(dropdown, { visibility: 'none', pointerEvents: 'none' })
      }
    }

    return () => {
      window.removeEventListener('resize', reposition)
      window.removeEventListener('click', clickHandler)
      window.removeEventListener('keyup', keyupHandler)
      observer?.disconnect()
    }
  }, [])

  React.useEffect(() => {
    toggle.current?.(open)
  }, [open])

  React.useEffect(() => {
    console.log(selected)

    if (open && searchable) {
      setInputValue(search)
    } else {
      setSearch('')
      setInputValue(getSelectedInputValue(selected))
    }
  }, [selected, open, searchable, search])

  const handleMultiChange = (value: string[]) => {
    setSelected(value)

    onChange?.(value)
  }

  const singleChange = (value: string) => {
    setSelected(value)

    setOpen(false)
    onChange?.(value)
  }

  React.useEffect(() => {
    const _value = value ?? (multiple ? [] : '')

    setSelected(_value)
  }, [value])

  return (
    <div
      ref={elementRef}
      data-select={id.current}
      className={classNames('select', 'text-field', className)}
    >
      <div
        className={classNames('select__input', 'text-field__input', {
          _dense: dense,
          _open: open
        })}
      >
        {label && !dense && (
          <label
            htmlFor={id.current}
            className={`${
              (multiple ? selected.length : selected) || open ? '_active' : ''
            }`}
          >
            {label}
            {required && <sup>*</sup>}
          </label>
        )}
        <input
          readOnly={!searchable || disabled}
          ref={inputRef}
          name={name}
          autoComplete="off"
          size={1}
          id={id.current}
          type={'text'}
          value={inputValue}
          onClick={() => {
            if (!disabled) setOpen(true)
          }}
          className={classNames({ _disabled: disabled })}
          onFocus={() => {
            if (searchable) {
              setInputValue('')
            }
          }}
          onChange={(e) => {
            setInputValue(e.target.value)
            setSearch(e.target.value)
          }}
        />

        <ReactSVG
          src={'/i/svg/chevron-down.svg'}
          wrapper={'span'}
          className={classNames('svg-icon select__icon', { _active: open })}
        ></ReactSVG>
      </div>

      <div
        data-select={id.current}
        ref={dropDownRef}
        className={'select-dropdown'}
      >
        <div className="select-dropdown__inner">
          <div className={'select-dropdown__options'}>
            {_options.length ? (
              _options.map((option) => {
                return (
                  <div
                    key={option?.value ?? option}
                    className={classNames('select-dropdown__option', {
                      _active: isOptionSelected(option)
                    })}
                  >
                    {multiple ? (
                      <CheckboxControl
                        value={selected}
                        name={option?.value ?? option}
                        onChange={(value) => {
                          handleMultiChange(value as string[])
                        }}
                      >
                        {option?.label ?? option}
                      </CheckboxControl>
                    ) : (
                      <span
                        onClick={() =>
                          singleChange(
                            fullValue ? option : option?.value ?? option
                          )
                        }
                      >
                        {option?.label ?? option}
                      </span>
                    )}
                  </div>
                )
              })
            ) : (
              <p
                className={
                  'text-body-small text-secondary-color text-align-center'
                }
              >
                Нет результатов
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Select
